// src/utils/auth.js

/**
 * Auth utility functions for handling JWT, user info, and roles.
 * Works across all pages (login, signup, profile, courses, videos, etc.)
 */

// Keys for localStorage
const TOKEN_KEY = "jy_token";
const USER_KEY = "jy_user";

/* ─────────── TOKEN ─────────── */

/**
 * Save JWT token to localStorage
 * @param {string} token
 */
export const saveToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  }
};

/**
 * Get JWT token from localStorage
 * @returns {string|null}
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/* ─────────── USER ─────────── */

/**
 * Save user info to localStorage
 * @param {object} user
 */
export const saveUser = (user) => {
  if (user && typeof user === "object") {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
};

/**
 * Get user info from localStorage
 * @returns {object|null}
 */
export const getUser = () => {
  try {
    const s = localStorage.getItem(USER_KEY);
    return s ? JSON.parse(s) : null;
  } catch (e) {
    console.error("Failed to parse user from localStorage", e);
    return null;
  }
};

/**
 * Remove user info and token from localStorage
 */
export const removeAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

/* ─────────── AUTH STATUS ─────────── */

/**
 * Check if user is logged in
 * @returns {boolean}
 */
export const isLoggedIn = () => {
  return !!getToken();
};

/**
 * Get logged in user's ID
 * @returns {number|null}
 */
export const getUserId = () => {
  const u = getUser();
  return u?.id ?? null;
};

/**
 * Get logged in user's role
 * @returns {string|null}
 */
export const getUserRole = () => {
  const u = getUser();
  return u?.role ?? null;
};

/* ─────────── LOGOUT ─────────── */

/**
 * Logout user (clears storage and optionally redirects)
 * @param {boolean} redirect - true to redirect to login page
 */
export const logout = (redirect = true) => {
  removeAuth();
  if (redirect) {
    window.location.href = "/login";
  }
};

/* ─────────── LOGIN ─────────── */

/**
 * Login user (save token & user info)
 * @param {string} token
 * @param {object} user
 */
export const loginUser = (token, user) => {
  saveToken(token);
  saveUser(user);
};

/* ─────────── SIGNUP ─────────── */

/**
 * Signup helper
 * Simply store user info if signup returns a JWT
 * @param {string} token
 * @param {object} user
 */
export const signupUser = (token, user) => {
  saveToken(token);
  saveUser(user);
};

/* ─────────── HELPER ─────────── */

/**
 * Get Authorization header for API calls
 * @returns {object}
 */
export const getAuthHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};
